import * as React from 'react'
import * as Sentry from '@sentry/react'
import { dataByTypename } from '../../../../client/utils/apollo-helpers'
import { useAtLeastOneFlag, useFlag, FF } from '@local/do-secundo-feature-flag'
import { track } from '@toasttab/do-secundo-analytics'
import { getRawPhoneNumberWithCountryCode } from '@local/do-secundo-form-utils'
import {
  accountLinkingStorage,
  storage as customerMgmtLocalStorage
} from '../../../../client/apollo/authentication/authentication-helpers'
import { AuthClient } from '@toasttab/guest-authentication-js'

import { config, defaultData } from '../../../../client/apollo/apollo-client'
import { cleanUpCartData } from '../../../../client/apollo/authentication/authentication-helpers'
import { useGetCart } from '../../../../client/components/CartQuery/CartQuery'

export interface inputShape {
  variables: {
    input: {
      source: string
    }
  }
}
interface attemptToCompleteProfileFirstArg {
  completeProfileCreation: (input: inputShape) => Promise<any>
  input: inputShape
}

export const attemptToCompleteProfile = async (
  { completeProfileCreation, input }: attemptToCompleteProfileFirstArg,
  maxNumberOfAttempts = 1,
  attemptsSoFar = 0
): Promise<any> => {
  try {
    attemptsSoFar++
    const result = await completeProfileCreation(input)

    // @todo fix this so that object property and typename match with passwordlessLogin client only mutation
    const { CompleteProfileCreationResponse, CompleteProfileCreationError } =
      dataByTypename(result.data.passwordlessLogin)

    if (CompleteProfileCreationError) {
      throw Error(CompleteProfileCreationError.message)
    }

    if (CompleteProfileCreationResponse) {
      return CompleteProfileCreationResponse
    }
  } catch (error) {
    if (attemptsSoFar >= maxNumberOfAttempts) {
      throw error
    } else {
      await new Promise((resolve) => setTimeout(resolve, attemptsSoFar * 250))
      return attemptToCompleteProfile(
        { completeProfileCreation, input },
        maxNumberOfAttempts,
        attemptsSoFar
      )
    }
  }
}

export interface handleVerificationCodeSubmitProps {
  code: string
  onCodeSubmissionSuccess: () => void
  completeProfileCreation: (input: inputShape) => Promise<any>
  trackCodeSubmissionSuccess: () => void
  from?: string
}

/**
 * Handles verification of sms code and callback when verification is complete
 */
export const handleVerificationCodeSubmit = async ({
  code,
  onCodeSubmissionSuccess,
  completeProfileCreation,
  trackCodeSubmissionSuccess,
  from
}: handleVerificationCodeSubmitProps) => {
  const input = {
    variables: {
      input: {
        code,
        source: 'ONLINE_ORDERING'
      }
    }
  }

  try {
    await attemptToCompleteProfile({ completeProfileCreation, input })
    trackCodeSubmissionSuccess()
    onCodeSubmissionSuccess()
  } catch (error) {
    if (error instanceof TypeError) {
      throw Error(
        `Toast is experiencing issues. Please continue to place your order without ${
          from === 'create' ? 'creating an account' : 'logging in'
        }.`
      )
    }
    throw error
  }
}

/**
 *   Determines presence of 'hasActiveSession' key in localStorage,
 *   which means user is/has been logged in at some point with a passwordless acct
 */
export const hasPasswordlessAccount = () => {
  const isPasswordlessAuthEnabledLocalStorageKey = 'hasActiveSession'
  return localStorage?.hasOwnProperty(isPasswordlessAuthEnabledLocalStorageKey)
}

/**
 *   Determines if 'hasActiveSession' key is in localStorage,
 *   and that they are signed in with in
 */
export const isPasswordlessLoggedIn = () => {
  const hasActiveSessionValue = localStorage?.getItem('hasActiveSession') || ''
  return hasPasswordlessAccount() && JSON.parse(hasActiveSessionValue)
}

const customerMgmtAuthLocalStorageName = 'auth'
export const hasExistingLegacyAccount = () =>
  localStorage?.hasOwnProperty(customerMgmtAuthLocalStorageName)

/**
 * Determines if someone is signed in with customermgmt
 * presence  of 'auth' in local storage means user is/has been logged in at some point with a customermgmt account
 */
export const isLegacyAccountLoggedIn = () =>
  Boolean(
    customerMgmtLocalStorage?.get('accessToken') &&
      customerMgmtLocalStorage?.get('refreshToken')
  )

/**
 * Copy of customermgmt access + refresh token
 * both required to trigger account linking function
 */
export const isLegacyAccountLoggedInForAccountLinking = () => {
  return Boolean(
    accountLinkingStorage?.get('accessToken') &&
      accountLinkingStorage?.get('refreshToken')
  )
}

/**
 * Util to determine whether or not to show user passwordless auth UI
 */
export const useIsPasswordlessAuthEnabled = (
  featureFlags = [
    FF.PASSWORDLESS_AUTH_DURING_CHECKOUT,
    FF.PASSWORDLESS_AUTH_AFTER_CHECKOUT
  ]
) => {
  const showLegacyAccount = false // return this when a legacy account should be shown

  // killswitch
  const killSwitch = useFlag(FF.PASSWORDLESS_AUTH_KILL_SWITCH)

  // ff flag
  const restaurantHasFeatureFlag = useAtLeastOneFlag(featureFlags)

  const shouldShowPasswordlessUx =
    hasPasswordlessAccount() ||
    (hasExistingLegacyAccount() &&
      !isLegacyAccountLoggedIn() &&
      restaurantHasFeatureFlag)

  const ACCOUNT_LINKING_FF_ENABLED = useFlag(FF.ACCOUNT_PROFILE_LINKING)

  if (killSwitch) return showLegacyAccount

  if (
    !ACCOUNT_LINKING_FF_ENABLED &&
    hasExistingLegacyAccount() &&
    !isPasswordlessLoggedIn()
  ) {
    return showLegacyAccount
  }
  if (shouldShowPasswordlessUx) return !showLegacyAccount // if a user has ever used pwless it should take precedence
  if (isLegacyAccountLoggedIn()) return showLegacyAccount // happens if user is signed in and hasn't used pwless
  return restaurantHasFeatureFlag // show based on FF
}

export const useTrackCodeSubmissionSuccess = () => {
  return React.useCallback((from: string) => {
    track('Passwordless Authentication Code Submission Success', {
      from
    })
  }, [])
}

/**
 * Custom hook that starts interaction with auth library. Must have phone and authClient to make the call.
 * Errors are sent to sentry. This can be used whenever we need to send the sms code. So currently this is used
 * in our pwlessCodeVerificationModal and our SmsConfirmationBenefits.
 * @param {string|undefined} phone ISO-formatted local time
 * @param {object} authClient our authentication library
 * @returns {useEffect hook}
 */
export const useSendSmsCode = (
  phone: string | undefined,
  authClient: object
) => {
  return React.useEffect(() => {
    if (phone && authClient) {
      try {
        authClient.requestPasswordless(getRawPhoneNumberWithCountryCode(phone))
      } catch (error: any) {
        Sentry.captureException(error)
        console.warn(error)
      }
    }
  }, [authClient, phone])
}

/**
 * React Hook that will log out a passwordless account user only if:
 * 1. the user is logged in and
 * 2. the kill switch is enabled
 */
export const useHandleKillSwitch = (killSwitchEnabled: boolean) => {
  const { cart } = useGetCart()
  const logout = React.useCallback(async () => {
    await config.authClient.logout()
    await cleanUpCartData(config.client, { cart })
    await config.client.clearStore()
    config.cache.writeQuery(defaultData)
  }, [killSwitchEnabled])

  React.useEffect(() => {
    if (killSwitchEnabled && isPasswordlessLoggedIn()) {
      logout()
    }
  }, [logout])
}
